import { AssetsSvgMap, TAssetsSvgMapValue, THandleClick } from '@libs/types';
import clsx from 'clsx';
import React, { ElementType } from 'react';
import { SvgComponent } from '../../../common/Svg/SvgComponent';
import { TConsumerItem } from '../../../types/data';
import { TransactionStatusEnum } from '../../../types/enums';
import { Visual } from '../../Image/Visual';
import { Tag } from '../Tag/Tag';
import { TextCommon } from '../Text/TextCommon';
import styles from './styles/rowTransaction.module.css';

type TRowTransaction = {
    as: ElementType;
    handleClick?: THandleClick;
    item: TConsumerItem;
};

export function RowTransaction({ item, as, handleClick }: TRowTransaction) {
    const isEnabled = item?.enabled ?? true;
    const title = item?.title;
    const subtitle = item?.subtitle;
    const additionalSubtitle = item?.additionalSubtitle;
    const currency = item?.currency;
    const direction = item?.direction;
    const iconName = item?.iconName;
    const avatar = item?.avatar;
    const flagIconSmall = item?.flagIconSmall;
    const statusFailed = item?.status ? item?.status === TransactionStatusEnum.FAIL : false;

    const getStatusIcon = (item: TConsumerItem) => {
        if (!item?.status) return;
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return 'ic_check_circle_small';
            case TransactionStatusEnum.FAIL:
                return 'ic_x_circle_small';
            default:
                return 'ic_clock_filled_small';
        }
    };

    const getIconColor = (item: TConsumerItem) => {
        if (!item?.status) return 'black';
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return item.direction === 'INCOME' ? 'purple_100' : 'black';
            case TransactionStatusEnum.FAIL:
                return 'grey_40_lc';
            default:
                return 'grey_72_lc';
        }
    };

    const getTextColor = (item: TConsumerItem) => {
        if (!item?.status) return 'black';
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return item.direction === 'INCOME' ? 'purple_100' : 'black';
            case TransactionStatusEnum.FAIL:
                return 'grey_40_lc';
            default:
                return 'grey_72_lc';
        }
    };

    const iconStatus = AssetsSvgMap[getStatusIcon(item) as keyof typeof AssetsSvgMap] ?? AssetsSvgMap['ic_clock_filled_small'];
    const amountPrefix = direction === 'INCOME' ? '+' : '-';
    const RowTransactionComponent = as ?? 'button';

    return (
        <RowTransactionComponent
            data-test={'rowTransaction_' + item.id}
            className={clsx(styles.commonRowTransaction, !isEnabled && styles.disabled)}
            onClick={handleClick}
        >
            {iconName && (
                <Visual
                    isWidget={false}
                    iconName={iconName}
                    rightMargin
                    iconHierarchy={'secondary'}
                    iconVariant={'circle'}
                    flagIconSmall={flagIconSmall}
                    size={'small'}
                    isEnabled={!statusFailed}
                />
            )}
            {avatar && <Visual isWidget={false} avatar={avatar} rightMargin flagIconSmall={flagIconSmall} size={'small'} isEnabled={!statusFailed} />}
            <div className={styles.transactionContent}>
                <div className={styles.transactionRow}>
                    <TextCommon isWidget={false} content={title} options={{ textColor: statusFailed ? 'grey_40_lc' : 'black', ellipsis: true }} />
                </div>
                {(subtitle || additionalSubtitle) && (
                    <div className={styles.transactionRow}>
                        <TextCommon
                            isWidget={false}
                            content={subtitle}
                            options={{
                                variant: 'secondary',
                                textColor: statusFailed ? 'grey_40_lc' : 'grey_72_lc',
                                ellipsis: true,
                            }}
                        />
                        {additionalSubtitle && (
                            <TextCommon
                                isWidget={false}
                                content={additionalSubtitle}
                                options={{
                                    variant: 'secondary',
                                    textColor: statusFailed ? 'grey_40_lc' : 'grey_72_lc',
                                    margin: { left: 5 },
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
            <div className={styles.transactionRight}>
                {item?.amount && (
                    <div className={clsx(styles.transactionRow, styles.alignRight)}>
                        <TextCommon
                            isWidget={false}
                            content={`${amountPrefix} ${item.amount} ${currency?.toLowerCase()}`}
                            options={{
                                variant: 'secondary',
                                textColor: getTextColor(item),
                                lineThrough: statusFailed,
                            }}
                            textTransformation={[
                                {
                                    text: item.amount,
                                    useLocaleNumberFormat: true,
                                },
                                {
                                    text: currency?.toLowerCase(),
                                    smallCaps: true,
                                },
                            ]}
                        />
                    </div>
                )}
                <div className={clsx(styles.transactionRow, styles.alignRight)}>
                    {!item?.metaText && item?.status && (
                        <SvgComponent
                            defaultName={'ic_clock_filled_small'}
                            name={iconStatus as TAssetsSvgMapValue}
                            width={16}
                            height={16}
                            fill={getIconColor(item)}
                        />
                    )}
                    {item?.metaText && <Tag metaText={item?.metaText} metaVariant={'tag'} />}
                </div>
            </div>
        </RowTransactionComponent>
    );
}
